import React from "react";
import { Divider, Flex, Text } from "@chakra-ui/react";
import CustomApprovalModal from "@/shared/components/custom-modal";
import AppButton from "@/features/line-sheet-sets/app-button";
import AppSelect from "@/features/line-sheet-sets/app-select";
import {
  PathPickFilterGroup,
  PathPickFilterGroupCard,
} from "@/features/pathpick/components/ui/PathPickFilterPanel";
import PathPickRequestFilterModal from "@/features/pathpick/components/request/PathPickRequestFilterModal";
import PathPickProductCardViewSettingsButton from "@/features/pathpick/components/product/PathPickProductCardViewSettingsButton";
import usePathPickProductCardView from "@/features/pathpick/hooks/usePathPickProductCardView";
import { PathPickMarketPriceBasis } from "@/features/pathpick/types";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";

interface SortOption<S extends string> {
  name: string;
  value: S;
}

interface Props<K extends string, S extends string> {
  isOpen: boolean;
  onClose: () => void;
  // 정렬·비교 기준 — 모달 안에서는 드래프트 값이다.
  sortOptions: SortOption<S>[];
  sortValue: S;
  onChangeSort: (value: S) => void;
  marketPriceBasis: PathPickMarketPriceBasis;
  onChangeMarketPriceBasis: (basis: PathPickMarketPriceBasis) => void;
  // 카드 보기 설정에서 비교 표시를 끄면 사라지는 조건을 화면별 필터 훅이 정리한다.
  onComparisonHidden: () => void;
  groups: PathPickFilterGroup<K>[];
  getSelectedValues: (key: K) => (string | number)[];
  onToggle: (key: K, value: string | number) => void;
  // 범위 필터처럼 그룹 카드로 표현할 수 없는 영역 — 그룹 카드 아래에 붙는다.
  extraCells?: React.ReactNode;
  onReset: () => void;
  onApply: () => void;
  isApplyDisabled?: boolean;
}

// 패스픽 모바일 필터 모달 셸 — 상품 찾기/담은 상품이 공유하는 프레젠테이셔널 컴포넌트.
// 좁은 폭에서 헤더에 줄줄이 쌓이던 정렬·비교 기준·카드 보기 설정·상세 필터를
// `필터` 버튼 하나 뒤로 모은다. 상태(드래프트)는 화면별 래퍼가 소유하고,
// 여기서 바꾼 값은 하단 `필터 적용`에서 한 번에 반영된다(닫으면 버려진다).
// 카드 보기 설정만은 자체 모달에서 바로 저장된다 — 데스크톱과 같은 진입점을 그대로 쓴다.
function PathPickMobileFilterModal<K extends string, S extends string>({
  isOpen,
  onClose,
  sortOptions,
  sortValue,
  onChangeSort,
  marketPriceBasis,
  onChangeMarketPriceBasis,
  onComparisonHidden,
  groups,
  getSelectedValues,
  onToggle,
  extraCells,
  onReset,
  onApply,
  isApplyDisabled = false,
}: Props<K, S>) {
  const { t } = useI18nHelper();
  // 비교 기준은 카드의 비교 정보를 어떤 가격으로 볼지라 비교 표시를 끄면 함께 감춘다.
  const { isComparisonVisible } = usePathPickProductCardView();

  return (
    <CustomApprovalModal
      isOpen={isOpen}
      onClose={onClose}
      // minWidth가 maxWidth를 이기고, 가운데 정렬된 모달은 왼쪽으로 넘치면 스크롤로도
      // 닿을 수 없어 화면 폭으로 클램프한다.
      minWidth={"min(420px, 90dvw)"}
      isCentered={true}
      // 브랜드 목록·범위 입력까지 들어가 길어지므로 본문만 스크롤하고 하단 적용 줄은 고정한다.
      scrollBehavior={"inside"}
    >
      <CustomApprovalModal.Header
        onClose={onClose}
        title={t("path_pick:filter.title")}
      />
      <CustomApprovalModal.Body>
        <Flex flexDirection={"column"} gap={"16px"}>
          <PathPickRequestFilterModal.Group label={t("path_pick:filter.sort")}>
            {/* 데스크톱 헤더와 같은 셀렉터. 옵션이 6개라 기본값이면 검색창이 떠 키보드가 올라오므로 끈다.
                width를 지정하면 matchWidth(트리거-드롭다운 너비 일치)가 꺼지므로 minWidth로 채운다. */}
            <AppSelect<S>
              name={sortOptions[0]?.name ?? ""}
              value={sortValue}
              options={sortOptions}
              isClearable={false}
              isSearchable={false}
              minWidth={"100%"}
              dropdownWidth={"100%"}
              onSelect={(value) => value && onChangeSort(value)}
            />
          </PathPickRequestFilterModal.Group>
          {isComparisonVisible && (
            <PathPickRequestFilterModal.Group
              label={t("path_pick:filter.market_basis")}
            >
              <Flex gap={"8px"}>
                {(["bidPrice", "tradePrice"] as PathPickMarketPriceBasis[]).map(
                  (basis) => (
                    <AppButton
                      key={basis}
                      variant={"white"}
                      isActive={marketPriceBasis === basis}
                      onClick={() => onChangeMarketPriceBasis(basis)}
                      chakraProps={{ padding: "4px 12px" }}
                    >
                      <Text textStyle={"body-sm-bold"} whiteSpace={"nowrap"}>
                        {t(
                          basis === "bidPrice"
                            ? "path_pick:filter.basis_bid"
                            : "path_pick:filter.basis_trade"
                        )}
                      </Text>
                    </AppButton>
                  )
                )}
              </Flex>
            </PathPickRequestFilterModal.Group>
          )}
          <Flex>
            <PathPickProductCardViewSettingsButton
              onComparisonHidden={onComparisonHidden}
              isEmphasized
            />
          </Flex>
          <Divider borderColor={"#EFEFED"} />
          {groups.map((group) => (
            <PathPickFilterGroupCard
              key={group.key}
              groupKey={group.key}
              title={group.title}
              options={group.options}
              singleAllValue={group.single?.allValue}
              selectedValues={getSelectedValues(group.key)}
              onToggle={onToggle}
            />
          ))}
          {extraCells}
        </Flex>
      </CustomApprovalModal.Body>
      <CustomApprovalModal.Footer
        cancelName={t("path_pick:filter.cancel")}
        confirmName={t("path_pick:filter.apply")}
        onCancel={onClose}
        onConfirm={onApply}
        isConfirmDisabled={isApplyDisabled}
        leftContent={
          <Text
            as={"button"}
            type={"button"}
            textStyle={"body-sm-medium"}
            color={"#444440"}
            _hover={{ color: "#1272EF" }}
            onClick={onReset}
          >
            {t("path_pick:filter.reset")}
          </Text>
        }
      />
    </CustomApprovalModal>
  );
}

export default PathPickMobileFilterModal;
